import * as React from 'react';
import { endOfMonth, format, startOfMonth, startOfWeek, subMonths } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { cn, toClinic } from '../../lib/utils';

/**
 * Date and time fields (DS-30…33, NFR-10).
 *
 * These are native `<input type="date|time">` controls on purpose. Reception
 * types a date faster than any calendar widget can be clicked, the browser
 * gives us the locale keypad and the picker for free, and no calendar library
 * enters the bundle. Rich month grids belong to the calendar screen, not here.
 */

const ISO_DATE = /^\d{4}-\d{2}-\d{2}/;

/**
 * NFR-10 — the value is already a clinic-local date string. Parsing it into a
 * `Date` and formatting it back would push it a day either way for anyone whose
 * browser is not on Amman time, so the date part is taken as written.
 */
function toDateField(value?: string | null): string {
  if (!value) return '';
  return ISO_DATE.test(value) ? value.slice(0, 10) : '';
}

function clinicToday(): Date {
  return toClinic(new Date()) ?? new Date();
}

const isoDate = (date: Date): string => format(date, 'yyyy-MM-dd');

// ---------------------------------------------------------------------------
// DatePicker
// ---------------------------------------------------------------------------

export interface DatePickerProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'defaultValue' | 'onChange' | 'type' | 'size'> {
  /** Clinic-local `yyyy-MM-dd` (an ISO datetime is accepted and truncated). */
  value?: string | null;
  /** `yyyy-MM-dd`, or null when the field is emptied. */
  onChange: (value: string | null) => void;
  min?: string;
  max?: string;
  /** Truthy renders the invalid state; the message itself belongs to `FieldError`. */
  error?: boolean | string;
}

export const DatePicker = React.forwardRef<HTMLInputElement, DatePickerProps>(function DatePicker(
  { value, onChange, min, max, error, className, ...rest },
  ref,
) {
  return (
    <input
      ref={ref}
      type="date"
      // DS-51 — the date segments are numbers and stay left-to-right in Arabic.
      dir="ltr"
      value={toDateField(value)}
      min={min}
      max={max}
      aria-invalid={error ? true : undefined}
      onChange={(event) => onChange(event.target.value || null)}
      data-testid="date-picker"
      className={cn('input-base font-mono', error && 'border-red-500 focus:ring-red-500', className)}
      {...rest}
    />
  );
});

// ---------------------------------------------------------------------------
// TimePicker
// ---------------------------------------------------------------------------

export interface TimePickerProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'defaultValue' | 'onChange' | 'type' | 'size'> {
  /** `HH:mm`, 24-hour — the shape PROV-04 working hours are stored in. */
  value?: string | null;
  onChange: (value: string | null) => void;
  min?: string;
  max?: string;
  error?: boolean | string;
}

export const TimePicker = React.forwardRef<HTMLInputElement, TimePickerProps>(function TimePicker(
  { value, onChange, min, max, error, className, ...rest },
  ref,
) {
  return (
    <input
      ref={ref}
      type="time"
      dir="ltr"
      value={value ?? ''}
      min={min}
      max={max}
      aria-invalid={error ? true : undefined}
      onChange={(event) => onChange(event.target.value || null)}
      className={cn('input-base font-mono', error && 'border-red-500 focus:ring-red-500', className)}
      {...rest}
    />
  );
});

// ---------------------------------------------------------------------------
// DateRangePicker
// ---------------------------------------------------------------------------

export interface DateRangeValue {
  from: string | null;
  to: string | null;
}

interface RangePreset {
  key: string;
  /** Key under `common:ui.datePicker` — the table is module-level, so it cannot
   *  hold a translated string of its own. */
  labelKey: string;
  build: () => DateRangeValue;
}

/**
 * INV-116 / RPT-* — every report opens on a period filter, and these five cover
 * what the doctor actually asks for. The clinic week starts Saturday, so the
 * week preset is anchored there rather than on the browser's locale.
 */
const PRESETS: RangePreset[] = [
  {
    key: 'today',
    labelKey: 'today',
    build: () => {
      const today = isoDate(clinicToday());
      return { from: today, to: today };
    },
  },
  {
    key: 'week',
    labelKey: 'thisWeek',
    build: () => {
      const today = clinicToday();
      return { from: isoDate(startOfWeek(today, { weekStartsOn: 6 })), to: isoDate(today) };
    },
  },
  {
    key: 'month',
    labelKey: 'thisMonth',
    build: () => {
      const today = clinicToday();
      return { from: isoDate(startOfMonth(today)), to: isoDate(today) };
    },
  },
  {
    key: 'last-month',
    labelKey: 'lastMonth',
    build: () => {
      const previous = subMonths(clinicToday(), 1);
      return { from: isoDate(startOfMonth(previous)), to: isoDate(endOfMonth(previous)) };
    },
  },
  {
    key: 'last-3-months',
    labelKey: 'last3Months',
    build: () => {
      const today = clinicToday();
      return { from: isoDate(subMonths(today, 3)), to: isoDate(today) };
    },
  },
];

export interface DateRangePickerProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange' | 'defaultValue'> {
  value: DateRangeValue;
  onChange: (value: DateRangeValue) => void;
  fromLabel?: string;
  toLabel?: string;
  min?: string;
  max?: string;
  error?: boolean | string;
  disabled?: boolean;
  /** The report screens want them; a compact filter row can turn them off. */
  showPresets?: boolean;
  /**
   * DS-30 — one dense row instead of the stacked block. A table toolbar sits on
   * a single line of `sm` controls, and the captioned two-row form leaves the
   * fields floating above it; inline moves the captions onto the fields.
   */
  inline?: boolean;
  /**
   * Inline only — puts From/To back on screen as leading text instead of leaving
   * them to `title`. A filter bar that lines its controls up on one row wants
   * the caption without the extra line the stacked form spends on it.
   */
  showCaptions?: boolean;
}

export function DateRangePicker({
  value,
  onChange,
  fromLabel: fromLabelProp,
  toLabel: toLabelProp,
  min,
  max,
  error,
  disabled,
  showPresets = true,
  inline = false,
  showCaptions = false,
  className,
  ...rest
}: DateRangePickerProps) {
  const { t } = useTranslation('common');
  const fromLabel = fromLabelProp ?? t('ui.datePicker.from');
  const toLabel = toLabelProp ?? t('ui.datePicker.to');

  // `yyyy-MM-dd` sorts lexicographically, so plain string comparison is the
  // whole clamp — no parsing, no timezone in the way.
  const setFrom = (from: string | null) => {
    const next: DateRangeValue = { from, to: value.to };
    if (from && next.to && next.to < from) next.to = from;
    onChange(next);
  };

  const setTo = (to: string | null) => {
    const next: DateRangeValue = { from: value.from, to };
    if (to && next.from && to < next.from) next.to = next.from;
    onChange(next);
  };

  const activePreset = React.useMemo(() => {
    if (!value.from || !value.to) return null;
    return PRESETS.find((preset) => {
      const range = preset.build();
      return range.from === value.from && range.to === value.to;
    })?.key ?? null;
  }, [value.from, value.to]);

  const presets = showPresets ? (
    <div className={cn('flex flex-wrap items-center', inline ? 'gap-0.5' : 'gap-1')}>
      {PRESETS.map((preset) => (
        <button
          key={preset.key}
          type="button"
          disabled={disabled}
          onClick={() => onChange(preset.build())}
          className={cn(
            'btn-ghost px-1.5 py-0.5 text-[10px]',
            activePreset === preset.key && 'bg-blue-50 text-blue-700',
          )}
        >
          {t(`ui.datePicker.${preset.labelKey}`)}
        </button>
      ))}
    </div>
  ) : null;

  if (inline) {
    // The caption becomes the accessible name — a toolbar has no room for it,
    // and `dd/mm/yyyy` already says what the field is. The field keeps the
    // system height so it lines up with the selects and search box beside it.
    // `showCaptions` puts the words back inline, which costs width but no height.
    const bound = (
      caption: string,
      current: string | null,
      commit: (next: string | null) => void,
      lowest?: string,
    ) => {
      const input = (
        <DatePicker
          aria-label={caption}
          title={caption}
          value={current}
          onChange={commit}
          min={lowest}
          max={max}
          error={error}
          disabled={disabled}
          className="w-[8.5rem]"
        />
      );
      if (!showCaptions) return input;
      return (
        <label className="flex items-center gap-1.5">
          <span className="label-micro">{caption}</span>
          {input}
        </label>
      );
    };

    return (
      <div className={cn('flex flex-wrap items-center gap-1.5', className)} {...rest}>
        <div className={cn('flex items-center', showCaptions ? 'gap-2.5' : 'gap-1')}>
          {bound(fromLabel, value.from, setFrom, min)}
          {/* The dash is what tells the two fields apart; the captions do that job
              themselves once they are on screen. */}
          {!showCaptions && (
            <span aria-hidden className="text-xs text-slate-400">
              —
            </span>
          )}
          {bound(toLabel, value.to, setTo, value.from ?? min)}
        </div>
        {presets}
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col gap-1.5', className)} {...rest}>
      <div className="flex items-end gap-2">
        <label className="flex-1">
          <span className="label-micro mb-0.5 block">{fromLabel}</span>
          <DatePicker
            value={value.from}
            onChange={setFrom}
            min={min}
            max={max}
            error={error}
            disabled={disabled}
          />
        </label>
        <label className="flex-1">
          <span className="label-micro mb-0.5 block">{toLabel}</span>
          <DatePicker
            value={value.to}
            onChange={setTo}
            min={value.from ?? min}
            max={max}
            error={error}
            disabled={disabled}
          />
        </label>
      </div>

      {presets}
    </div>
  );
}
